import { useState, useEffect, useRef, useCallback } from 'react';
import { Sparkles, X, Send, MessageCircle, Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import api from '@/utils/api';
import axios from 'axios';

/**
 * XVAssistWidget — Persistent bottom-right rugby AI mentor (v7.6 §42).
 *
 * Two modes:
 *  - Authenticated: reads role + live context, hits /xv-assist/chat
 *  - Public:        no auth, hits /xv-assist/public/chat (homepage pre-login)
 *
 * Auto-opens on first visit via custom event `xv-assist:open` from any page.
 */
export default function XVAssistWidget({ forcePublic = false }) {
  const { user } = useAuth();
  const isAuthed = !forcePublic && !!user;
  const mode = isAuthed ? 'auth' : 'public';

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [contextLoaded, setContextLoaded] = useState(false);
  const [opening, setOpening] = useState('');
  const [chips, setChips] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [role, setRole] = useState(mode === 'auth' ? (user?.role || 'player') : 'public');
  const [messages, setMessages] = useState([]); // [{sender, text}]
  const [input, setInput] = useState('');
  const scrollRef = useRef(null);

  const baseURL = process.env.REACT_APP_BACKEND_URL;

  // Listen for global "open widget" event (from homepage CTAs)
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener('xv-assist:open', handler);
    return () => window.removeEventListener('xv-assist:open', handler);
  }, []);

  // Auto-open on public homepage after 4s (once per visitor via localStorage)
  useEffect(() => {
    if (mode === 'public') {
      const seen = localStorage.getItem('xv-assist-seen');
      if (!seen) {
        const t = setTimeout(() => {
          setOpen(true);
          localStorage.setItem('xv-assist-seen', '1');
        }, 4000);
        return () => clearTimeout(t);
      }
    }
  }, [mode]);

  const fetchContext = useCallback(async () => {
    if (contextLoaded) return;
    setLoading(true);
    try {
      let data;
      if (mode === 'auth') {
        const res = await api.get('/xv-assist/context');
        data = res.data;
      } else {
        const res = await axios.get(`${baseURL}/api/xv-assist/public/context`);
        data = res.data;
      }
      setOpening(data.opening_message);
      setChips(data.chips || []);
      setSessionId(data.session_id);
      setRole(data.role);
      setMessages([{ sender: 'assistant', text: data.opening_message }]);
      setContextLoaded(true);
    } catch (e) {
      setMessages([{ sender: 'assistant', text: "XV Assist couldn't load right now — try again in a moment." }]);
    } finally {
      setLoading(false);
    }
  }, [mode, baseURL, contextLoaded]);

  useEffect(() => {
    if (open) fetchContext();
  }, [open, fetchContext]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const sendMessage = async (text) => {
    if (!text.trim() || sending) return;
    const userMsg = { sender: 'user', text };
    setMessages((m) => [...m, userMsg]);
    setInput('');
    setSending(true);
    try {
      const payload = { text, session_id: sessionId };
      let reply;
      if (mode === 'auth') {
        const res = await api.post('/xv-assist/chat', payload);
        reply = res.data.reply;
        if (res.data.session_id) setSessionId(res.data.session_id);
      } else {
        const res = await axios.post(`${baseURL}/api/xv-assist/public/chat`, payload);
        reply = res.data.reply;
        if (res.data.session_id) setSessionId(res.data.session_id);
      }
      setMessages((m) => [...m, { sender: 'assistant', text: reply }]);
    } catch (e) {
      setMessages((m) => [...m, { sender: 'assistant', text: "I hit a snag answering that. Try rephrasing, or contact info@futurexv.co.za if it persists." }]);
    } finally {
      setSending(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  return (
    <>
      {/* Floating avatar button */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="xv-assist-avatar"
          aria-label="Ask XV Assist"
          className="fixed bottom-4 right-4 z-50 group flex items-end gap-3"
        >
          <span className="hidden sm:inline-block bg-zinc-900/90 backdrop-blur border border-zinc-700 text-zinc-100 text-xs font-semibold px-4 py-2 rounded-full shadow-lg group-hover:border-[#A3FF00]/70 transition-colors mb-2">
            Ask XV Assist
          </span>
          <span className="relative w-[78px] h-[110px] block">
            <img
              src="/xv_assist_avatar.png"
              alt="XV Assist"
              className="absolute inset-0 w-full h-full object-contain drop-shadow-[0_0_18px_rgba(163,255,0,0.45)] group-hover:drop-shadow-[0_0_28px_rgba(163,255,0,0.7)] group-hover:scale-105 active:scale-95 transition-all"
            />
            {mode === 'auth' && (
              <span className="absolute top-1 right-1 w-3 h-3 rounded-full bg-green-400 border-2 border-black z-10" aria-hidden />
            )}
          </span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="xv-assist-panel"
          className="fixed bottom-4 right-4 z-50 w-[min(380px,calc(100vw-2rem))] max-h-[min(640px,calc(100vh-2rem))] bg-zinc-950 border border-zinc-800 rounded-t-2xl rounded-bl-2xl rounded-br-sm shadow-2xl flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800 bg-gradient-to-r from-[#0f1607] via-zinc-950 to-zinc-950">
            <div className="flex items-center gap-2.5 min-w-0">
              <span className="relative w-11 h-11 shrink-0">
                <img src="/xv_assist_avatar.png" alt="XV Assist" className="absolute inset-0 w-full h-full object-contain drop-shadow-[0_0_8px_rgba(163,255,0,0.35)]" />
                {mode === 'auth' && (
                  <span className="absolute top-0 right-0 w-2.5 h-2.5 rounded-full bg-green-400 border-2 border-zinc-950" />
                )}
              </span>
              <div className="min-w-0">
                <p className="text-white font-bold text-sm leading-tight flex items-center gap-1.5">
                  XV Assist
                  <span className="text-[10px] bg-[#A3FF00]/15 text-[#A3FF00] px-1.5 py-0.5 rounded font-bold">AI</span>
                </p>
                <p className="text-zinc-500 text-[11px] leading-tight truncate">
                  {mode === 'auth' ? `${role} dashboard` : 'Ask anything about FutureXV'}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="xv-assist-close"
              aria-label="Close"
              className="text-zinc-500 hover:text-white p-1.5 rounded-md hover:bg-zinc-900"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-[200px]" data-testid="xv-assist-messages">
            {loading && (
              <div className="flex items-center gap-2 text-zinc-500 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading your context…
              </div>
            )}
            {messages.map((m, i) => (
              <div key={`${m.sender}-${i}-${(m.text || '').slice(0, 16)}`} className={`flex ${m.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] text-sm leading-relaxed rounded-2xl px-3.5 py-2.5 whitespace-pre-wrap break-words ${
                    m.sender === 'user'
                      ? 'bg-[#A3FF00] text-black rounded-br-md'
                      : 'bg-zinc-900 text-zinc-100 border border-zinc-800 rounded-bl-md'
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex items-center gap-2 text-zinc-500 text-xs pl-1">
                <Loader2 className="w-3 h-3 animate-spin" />XV Assist is thinking…
              </div>
            )}
          </div>

          {/* Quick chips (only shown until user sends first message) */}
          {messages.length <= 1 && chips.length > 0 && !sending && (
            <div className="px-4 pb-2 flex flex-wrap gap-1.5" data-testid="xv-assist-chips">
              {chips.map((c, i) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => sendMessage(c.replace(' →', ''))}
                  className="text-xs bg-zinc-900 border border-zinc-700 text-zinc-200 hover:border-[#A3FF00] hover:text-[#A3FF00] px-3 py-1.5 rounded-full transition-colors"
                  data-testid={`xv-assist-chip-${i}`}
                >
                  {c}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <form onSubmit={handleSubmit} className="border-t border-zinc-800 p-3 flex items-center gap-2 bg-zinc-950" data-testid="xv-assist-form">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={mode === 'auth' ? 'Ask anything…' : 'How can I help?'}
              className="flex-1 bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#A3FF00]/60"
              data-testid="xv-assist-input"
              disabled={sending}
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              className="bg-[#A3FF00] hover:bg-[#88dd00] disabled:opacity-40 disabled:cursor-not-allowed text-black p-2 rounded-lg transition-colors"
              data-testid="xv-assist-send"
              aria-label="Send"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </form>
        </div>
      )}
    </>
  );
}
